import { useState, useMemo } from 'react';
import { usePipelineNodeRelationships, type PipelineNodeRelationship } from '../../hooks/usePipelineNodeRelationships';
import { usePipelineNodes } from '../../hooks/usePipelineNodes';
import { pipelineNodeRelationshipService } from '../../services/pipelineNodeRelationship.service';
import { toastService } from '../../services/toast.service';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Loader2, RefreshCw } from 'lucide-react';
import { useIsReadOnly } from '../../contexts/ReadOnlyContext';

interface RelationshipsTabProps {
  nodeId: string;
}

export function RelationshipsTab({ nodeId }: RelationshipsTabProps) {
  const readOnly = useIsReadOnly();
  const queryClient = useQueryClient();
  const { data: relationships, isLoading: isLoadingRels } = usePipelineNodeRelationships();
  const { data: allNodesData, isLoading: isLoadingNodes } = usePipelineNodes({
    page: 1,
    pageSize: 1000,
  });
  const [reprocessingIds, setReprocessingIds] = useState<Set<string>>(new Set());

  const nodeNameMap = useMemo(() => {
    const map = new Map<string, string>();
    if (allNodesData?.records) {
      for (const n of allNodesData.records) {
        map.set(n.id, n.label || n.name || n.id);
      }
    }
    return map;
  }, [allNodesData?.records]);

  const nodeRelationships = useMemo(() => {
    if (!relationships) return [];
    return relationships.filter(
      (rel) => rel.parent_node_id === nodeId || rel.child_node_id === nodeId
    );
  }, [relationships, nodeId]);

  const handleReprocess = async (rel: PipelineNodeRelationship) => {
    setReprocessingIds((prev) => new Set(prev).add(rel.id));
    try {
      await pipelineNodeRelationshipService.save(rel);
      await queryClient.invalidateQueries({ queryKey: ['pipeline-node-relationships'] });
      await queryClient.invalidateQueries({ queryKey: ['pipeline-node', rel.child_node_id] });
      await queryClient.invalidateQueries({ queryKey: ['pipeline-node', rel.parent_node_id] });
      toastService.success(`Reprocessed relationship "${rel.name}"`);
    } catch (err) {
      console.error('Failed to reprocess relationship:', err);
      toastService.error('Failed to reprocess relationship', {
        description: err instanceof Error ? err.message : 'An unexpected error occurred',
      });
    } finally {
      setReprocessingIds((prev) => {
        const next = new Set(prev);
        next.delete(rel.id);
        return next;
      });
    }
  };

  if (isLoadingRels || isLoadingNodes) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (nodeRelationships.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-8 text-center">
        No relationships found for this entity.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Relationships this entity participates in. Reprocessing a relationship will re-save it, which reconciles foreign key fields and fixes any data integrity issues.
      </p>
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Parent</TableHead>
              <TableHead>Child</TableHead>
              <TableHead>Foreign Key</TableHead>
              <TableHead className="w-[140px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {nodeRelationships.map((rel) => {
              const isParent = rel.parent_node_id === nodeId;
              const isReprocessing = reprocessingIds.has(rel.id);

              return (
                <TableRow key={rel.id}>
                  <TableCell className="font-medium">{rel.name}</TableCell>
                  <TableCell>
                    <Badge variant={isParent ? 'default' : 'secondary'}>
                      {isParent ? 'Parent' : 'Child'}
                    </Badge>
                  </TableCell>
                  <TableCell>{nodeNameMap.get(rel.parent_node_id) || rel.parent_node_id}</TableCell>
                  <TableCell>{nodeNameMap.get(rel.child_node_id) || rel.child_node_id}</TableCell>
                  <TableCell className="font-mono text-xs">{rel.child_foreign_key_name}</TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleReprocess(rel)}
                      disabled={readOnly || isReprocessing}
                    >
                      {isReprocessing ? (
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3 w-3 mr-1" />
                      )}
                      {isReprocessing ? 'Reprocessing...' : 'Reprocess'}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
